import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import { IconButton, Tooltip } from '@mui/material'
import { ReactComponent as RentalFeeGracePeriodIcon } from 'assets/RentalFeeGracePeriod.svg'

import { get } from 'utils/lodash'
import { sortByDateSelector } from 'data/utils/sortByDateSelector'
import {
  useGetRentalFeesResetHistoryQuery,
  useGetRentalFeesByConfiguredServiceQuery,
} from 'api/configured-service/configuredServiceRentalFeesCrud'
import { useGetAllRentalPeriodVersionsQuery, useGetRentalFeeToConfiguredServiceVersionsQuery } from 'api/entity-version/entityVersionCrud'

import T from 'T'
import StyledBadge from 'components/notes/common/StyledBadge'
import { getRows, normalizeAllPeriodVersionsToRows, normalizePriceChangeVersionsToRows } from '../popover/grace-period-reset-history/utils'
import GracePeriodResetHistoryPopover from '../popover/grace-period-reset-history/GracePeriodResetHistoryPopover'
import GracePeriodResetHistoryDialog from '../popover/grace-period-reset-history/GracePeriodResetHistoryDialog'

const GracePeriodBadgeButton = ({ size = 'small', rentalFeeResetCount = 0, configuredServiceId, showTooltip = false, ...rest }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [isOpenGracePeriodResetHistoryDialog, setIsOpenGracePeriodResetHistoryDialog] = useState(false)

  const { isFetching: isResetLoading, data: resetHistoryData } = useGetRentalFeesResetHistoryQuery(
    { id: configuredServiceId },
    { skip: !configuredServiceId }
  )
  const { data: rentalFeeData } = useGetRentalFeesByConfiguredServiceQuery({ id: configuredServiceId }, { skip: !configuredServiceId })
  const rentalFeeToConfiguredServiceId = get(rentalFeeData, 'configuredRentalFees[0].id')

  const { isFetching: isPeriodVersionsLoading, data: allPeriodVersionsData } = useGetAllRentalPeriodVersionsQuery(
    { rentalFeeToConfiguredServiceId },
    { skip: !rentalFeeToConfiguredServiceId }
  )
  const { isFetching: isPriceChangeLoading, data: priceChangeVersionsData } = useGetRentalFeeToConfiguredServiceVersionsQuery(
    { rentalFeeToConfiguredServiceId },
    { skip: !rentalFeeToConfiguredServiceId, refetchOnMountOrArgChange: true }
  )

  const graceRows = useMemo(() => getRows(resetHistoryData).map(row => ({ ...row, type: 'grace_reset' })), [resetHistoryData])
  const periodVersionRows = useMemo(() => normalizeAllPeriodVersionsToRows(allPeriodVersionsData), [allPeriodVersionsData])
  const priceChangeRows = useMemo(() => normalizePriceChangeVersionsToRows(priceChangeVersionsData), [priceChangeVersionsData])
  const mergedHistoryRows = useMemo(() => {
    const merged = [...graceRows, ...periodVersionRows, ...priceChangeRows]
    return sortByDateSelector({ data: merged, key: 'createdAt' })
  }, [graceRows, periodVersionRows, priceChangeRows])
  const isHistoryLoading = isResetLoading || isPeriodVersionsLoading || isPriceChangeLoading

  const historyActivityCount = useMemo(
    () => (mergedHistoryRows.length > 0 ? mergedHistoryRows.length : rentalFeeResetCount),
    [mergedHistoryRows.length, rentalFeeResetCount]
  )
  const hasHistory = historyActivityCount > 0

  const handlePopoverOpen = event => setAnchorEl(event.currentTarget)

  const handlePopoverClose = () => setAnchorEl(null)

  const handleOpenGracePeriodResetHistoryDialog = () => {
    setIsOpenGracePeriodResetHistoryDialog(true)
  }

  const handleCloseGracePeriodResetHistoryDialog = () => {
    setIsOpenGracePeriodResetHistoryDialog(false)
  }

  return (
    <>
      <Tooltip
        title={T.GRACE_PERIOD_RESET_HISTORY}
        arrow
        placement="bottom"
        disableHoverListener={hasHistory || !showTooltip}
        disableInteractive
      >
        <span>
          <IconButton
            size={size}
            disabled={!hasHistory}
            onClick={handleOpenGracePeriodResetHistoryDialog}
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
            {...rest}
          >
            <StyledBadge badgeContent={historyActivityCount}>
              <RentalFeeGracePeriodIcon />
            </StyledBadge>
          </IconButton>
        </span>
      </Tooltip>

      {anchorEl && (
        <GracePeriodResetHistoryPopover
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          rows={mergedHistoryRows}
          isLoading={isHistoryLoading}
        />
      )}
      <GracePeriodResetHistoryDialog
        isOpen={isOpenGracePeriodResetHistoryDialog}
        onClose={handleCloseGracePeriodResetHistoryDialog}
        rows={mergedHistoryRows}
        isLoading={isHistoryLoading}
      />
    </>
  )
}

GracePeriodBadgeButton.propTypes = {
  size: PropTypes.string,
  rentalFeeResetCount: PropTypes.number,
  configuredServiceId: PropTypes.string.isRequired,
  showTooltip: PropTypes.bool,
}

export default GracePeriodBadgeButton
